import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithMutation, queryClientAtom } from 'jotai-tanstack-query';

import { postCopyTradeApplyMasterApi } from '@flipster/apis/v1/copy-trade';
import { ProfileSettingVisibility } from '@flipster/apis/v1/user/types';
import { T } from '@flipster/i18n';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorMessage } from '@flipster/utils/error';

const defaultProfitSharePct = '15';

export const applyMasterTraderBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);

  // Form field atoms
  const nameAtom = atom('');
  const socialMediaLinksAtom = atom('');
  const agreeToTermsAndPolicyAtom = atom(false);
  const agreeToCopyTradingTermsAtom = atom(false);
  const profitSharePctAtom = atom(defaultProfitSharePct);
  const isProfitShareEditableAtom = atom(false);
  const visibilityAtom = atom<ProfileSettingVisibility>('PUBLIC');
  const entryCodeAtom = atom('');

  type EntryCodeError = 'special-characters' | 'length';
  const entryCodeErrorAtom = atom<EntryCodeError | undefined>((get) => {
    const visibility = get(visibilityAtom);
    if (visibility !== 'PRIVATE') return;
    const entryCode = get(entryCodeAtom);
    if (entryCode.length === 0) return;
    if (/[^a-zA-Z0-9]/.test(entryCode)) return 'special-characters';
    if (entryCode.length < 4 || entryCode.length > 12) return 'length';
  });

  // Derived atom for form validity
  const isFormValidAtom = atom((get) => {
    const name = get(nameAtom);
    const socialMediaLinks = get(socialMediaLinksAtom);
    const agreeToTermsAndPolicy = get(agreeToTermsAndPolicyAtom);
    const agreeToCopyTradingTerms = get(agreeToCopyTradingTermsAtom);
    const visibility = get(visibilityAtom);
    const entryCode = get(entryCodeAtom);

    const baseValid =
      name.trim().length > 0 && socialMediaLinks.trim().length > 0 && agreeToTermsAndPolicy && agreeToCopyTradingTerms;

    if (visibility === 'PRIVATE') {
      if (get(entryCodeErrorAtom)) return false;
      if (entryCode.length < 4) return false;
    }

    return baseValid;
  });

  // Mutation atom for form submission
  const submitMutationAtom = atomWithMutation(() => ({
    mutationKey: ['postCopyTradeApplyMasterApi'],
    mutationFn: postCopyTradeApplyMasterApi,
  }));

  // Derived atom for loading state
  const isLoadingAtom = atom((get) => get(submitMutationAtom).isPending);

  function enableProfitShareEdit() {
    store.set(isProfitShareEditableAtom, true);
  }

  function resetProfitShareToRecommended() {
    store.set(profitSharePctAtom, defaultProfitSharePct);
    store.set(isProfitShareEditableAtom, false);
  }

  // Reset form function
  function resetForm() {
    store.set(nameAtom, '');
    store.set(socialMediaLinksAtom, '');
    store.set(agreeToTermsAndPolicyAtom, false);
    store.set(agreeToCopyTradingTermsAtom, false);
    store.set(profitSharePctAtom, defaultProfitSharePct);
    store.set(isProfitShareEditableAtom, false);
    store.set(visibilityAtom, 'PUBLIC');
    store.set(entryCodeAtom, '');
  }

  // Submit form function
  async function submitForm() {
    const name = store.get(nameAtom);
    const socialMediaLink = store.get(socialMediaLinksAtom);
    const profitSharePct = store.get(profitSharePctAtom);
    const visibility = store.get(visibilityAtom);
    const invitationCode = visibility === 'PRIVATE' ? store.get(entryCodeAtom) : null;

    try {
      const { mutateAsync } = store.get(submitMutationAtom);
      await mutateAsync({
        name,
        socialMediaLink,
        profitSharePct,
        volumeRebatePct: '5',
        invitationCode,
      });

      snackbar.success({ message: <T id='Copy.r260130.Flipster_master_trader_program_application_submitted' /> });
      resetForm();

      const queryClient = store.get(queryClientAtom);
      await queryClient.invalidateQueries({ queryKey: ['postCopyTradeApplyMasterApi'] });
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
    }
  }

  return {
    nameAtom,
    socialMediaLinksAtom,
    agreeToTermsAndPolicyAtom,
    agreeToCopyTradingTermsAtom,
    profitSharePctAtom,
    isProfitShareEditableAtom,
    visibilityAtom,
    entryCodeAtom,
    entryCodeErrorAtom,
    isFormValidAtom,
    isLoadingAtom,
    submitForm,
    resetForm,
    enableProfitShareEdit,
    resetProfitShareToRecommended,
  };
});
applyMasterTraderBunja.debugLabel = 'applyMasterTraderBunja';
